/**
 * Platformer collision foundation — a small Godot-flavoured collision
 * model layered on top of the existing AABB tile-grid physics.
 *
 * Three pieces, all framework-free and pure (no Pixi, no DOM) so they
 * can be unit-reasoned and shared by the runtime AND the editor:
 *
 *   1. Collision LAYERS + MASKS (bitmask) — every collision object
 *      lives on one or more `collisionLayer` bits and scans for
 *      contacts on its `collisionMask` bits. Two objects interact when
 *      either one scans a bit the other occupies (the Godot rule:
 *      `A.mask & B.layer || B.mask & A.layer`). This lets a designer
 *      make, say, a "ghost" enemy the player walks through, or coins
 *      that only the player can grab.
 *
 *   2. Collision SHAPES — rect / circle (+ polygon stored, runtime
 *      approximated by its bounding box for v1). `worldShape()` turns
 *      an entity's box + shape def into world-space geometry and
 *      `shapesOverlap()` tests two of them. A default rect at scale 1
 *      is byte-for-byte the old `aabbOverlap`, so existing stages are
 *      unaffected until a designer changes a shape.
 *
 *   3. ONE-WAY platforms — a tile layer flagged one-way only stops a
 *      body that is FALLING onto its top edge; the body jumps up
 *      through it and walks past it horizontally. `oneWayBlocksDown()`
 *      encodes that "feet were above the platform top last frame" test
 *      the Y-axis resolver uses.
 *
 * This module is the seam the rest of Phase 2 (shape draw tools,
 * Area2D-style triggers, the layer/mask matrix UI) plugs into.
 */

// ── Collision layers (bitmask) ────────────────────────────────────

/** Number of named collision layers the model exposes (Godot uses 32;
 *  8 is plenty for a 2-D platformer and keeps the matrix UI compact). */
export const COLLISION_LAYER_COUNT = 8;

/** Default human names for the 8 layers, indexed by bit position. A
 *  stage may override these in `stage.collision.layers`. */
export const DEFAULT_COLLISION_LAYER_NAMES = Object.freeze([
    'World', 'Player', 'Enemy', 'Item', 'Hazard', 'Trigger', 'Decoration', 'Custom',
]);

/** Named single-bit constants for the default layout, used by the
 *  per-entity collision defaults below. `bit(i)` builds an arbitrary
 *  one. */
export const COLLISION_BITS = Object.freeze({
    world:   1 << 0,
    player:  1 << 1,
    enemy:   1 << 2,
    item:    1 << 3,
    hazard:  1 << 4,
    trigger: 1 << 5,
    deco:    1 << 6,
    custom:  1 << 7,
});

/** The single-bit value for layer index `i` (0-based). */
export function bit(i) {
    return 1 << i;
}

/**
 * Godot's interaction rule: two collision objects interact when either
 * one scans (masks) a layer the other occupies. Objects that haven't
 * been given an explicit layer + mask are treated as "interact with
 * everything" so legacy stages (no collision config) keep their old
 * collide-with-all-entities behaviour.
 *
 * @param {{collisionLayer?:number, collisionMask?:number}} a
 * @param {{collisionLayer?:number, collisionMask?:number}} b
 * @returns {boolean}
 */
export function masksInteract(a, b) {
    const al = a?.collisionLayer, am = a?.collisionMask;
    const bl = b?.collisionLayer, bm = b?.collisionMask;
    // Unconfigured on either side → legacy "always collide".
    if (al == null || am == null || bl == null || bm == null) return true;
    return ((am & bl) !== 0) || ((bm & al) !== 0);
}

/**
 * Per-entity-type default collision layer + mask. Chosen so the
 * default set reproduces the original "player collides with every
 * entity" behaviour exactly:
 *   - the player scans Enemy + Item + Hazard + Trigger,
 *   - each entity occupies the matching layer,
 * so `masksInteract(player, entity)` is true for every pair until a
 * designer narrows it.
 */
export const DEFAULT_ENTITY_COLLISION = Object.freeze({
    'player.spawn': { layer: COLLISION_BITS.player,  mask: COLLISION_BITS.enemy | COLLISION_BITS.item | COLLISION_BITS.hazard | COLLISION_BITS.trigger },
    'goomba':       { layer: COLLISION_BITS.enemy,   mask: COLLISION_BITS.world },
    'koopa':        { layer: COLLISION_BITS.enemy,   mask: COLLISION_BITS.world },
    'coin':         { layer: COLLISION_BITS.item,    mask: 0 },
    'mushroom':     { layer: COLLISION_BITS.item,    mask: COLLISION_BITS.world },
    'flag.goal':    { layer: COLLISION_BITS.trigger, mask: 0 },
    'checkpoint':   { layer: COLLISION_BITS.trigger, mask: 0 },
    // Area2D-style trigger zone: a non-solid detection region the
    // player walks into (the player's default mask scans Trigger).
    'area.trigger': { layer: COLLISION_BITS.trigger, mask: 0 },
    // Particle emitter: purely visual, sits on Decoration which the
    // player's default mask ignores, so it never interacts.
    'fx.emitter':   { layer: COLLISION_BITS.deco, mask: 0 },
    // Point light: a lighting-only marker on Decoration; never collides.
    'light.point':  { layer: COLLISION_BITS.deco, mask: 0 },
    // Music Note: a pass-through pickup-style sensor. Lives on Item so
    // the player's default mask scans it (same setup as Coin / Mushroom)
    // and the scene's overlap sweep routes the verdict to PlatformerNotes.
    'note':         { layer: COLLISION_BITS.item, mask: 0 },
});

/** Default { layer, mask } for an entity type, falling back to a
 *  plain World object for unknown types. */
export function defaultCollisionFor(type) {
    return DEFAULT_ENTITY_COLLISION[type] || { layer: COLLISION_BITS.world, mask: COLLISION_BITS.world };
}

// ── Collision shapes ──────────────────────────────────────────────

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Build a world-space collision shape from a body box + a shape def.
 *
 * @param {{x:number,y:number,w:number,h:number}} box  the entity's AABB
 * @param {{type?:string, scale?:number}} [def]        shape descriptor
 *        (`type`: 'rect' | 'circle' | 'polygon'; `scale`: 0.05..1 inset
 *        of the box, default 1 = full box).
 * @returns {{type:'rect',x,y,w,h} | {type:'circle',cx,cy,r}}
 */
export function worldShape(box, def) {
    const scale = def && Number.isFinite(def.scale) ? clamp(def.scale, 0.05, 1) : 1;
    const type = def?.type || 'rect';
    const w = box.w * scale;
    const h = box.h * scale;
    const x = box.x + (box.w - w) / 2;
    const y = box.y + (box.h - h) / 2;
    if (type === 'circle') {
        return { type: 'circle', cx: box.x + box.w / 2, cy: box.y + box.h / 2, r: Math.min(w, h) / 2 };
    }
    if (type === 'polygon' && Array.isArray(def.points) && def.points.length >= 3) {
        // `def.points` are box-relative (0..1); map them into world
        // space. A polygon ignores `scale` — its vertices already
        // describe the exact shape.
        const points = def.points.map((p) => ({
            x: box.x + clamp(p.x, 0, 1) * box.w,
            y: box.y + clamp(p.y, 0, 1) * box.h,
        }));
        return { type: 'polygon', points };
    }
    // rect — and polygon with no/too-few points falls back to its box.
    return { type: 'rect', x, y, w, h };
}

/** The four corner points of a rect, clockwise — lets the polygon
 *  routines treat a rect uniformly via SAT. */
function rectToPolygon(r) {
    return [
        { x: r.x,         y: r.y },
        { x: r.x + r.w,   y: r.y },
        { x: r.x + r.w,   y: r.y + r.h },
        { x: r.x,         y: r.y + r.h },
    ];
}

/** Project a point set onto an axis (unit-ish vector); returns the
 *  [min, max] scalar interval. */
function projectPolygon(points, ax, ay) {
    let min = Infinity, max = -Infinity;
    for (const p of points) {
        const d = p.x * ax + p.y * ay;
        if (d < min) min = d;
        if (d > max) max = d;
    }
    return [min, max];
}

/** Separating Axis Theorem overlap for two CONVEX polygons. Tests the
 *  edge normals of both; a gap on any axis means no overlap. */
function polygonsOverlap(a, b) {
    for (const poly of [a, b]) {
        for (let i = 0; i < poly.length; i++) {
            const p1 = poly[i];
            const p2 = poly[(i + 1) % poly.length];
            // Edge normal (perpendicular to the edge).
            const ax = -(p2.y - p1.y);
            const ay = (p2.x - p1.x);
            const [minA, maxA] = projectPolygon(a, ax, ay);
            const [minB, maxB] = projectPolygon(b, ax, ay);
            if (maxA < minB || maxB < minA) return false;
        }
    }
    return true;
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function distSqPointSegment(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
    t = clamp(t, 0, 1);
    const cx = ax + t * dx, cy = ay + t * dy;
    const ex = px - cx, ey = py - cy;
    return ex * ex + ey * ey;
}

/** Even-odd point-in-polygon test. */
function pointInPolygon(px, py, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y;
        const xj = poly[j].x, yj = poly[j].y;
        const intersect = ((yi > py) !== (yj > py))
            && (px < ((xj - xi) * (py - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

function circleVsPolygon(c, poly) {
    if (pointInPolygon(c.cx, c.cy, poly)) return true;
    const rSq = c.r * c.r;
    for (let i = 0; i < poly.length; i++) {
        const p1 = poly[i];
        const p2 = poly[(i + 1) % poly.length];
        if (distSqPointSegment(c.cx, c.cy, p1.x, p1.y, p2.x, p2.y) <= rSq) return true;
    }
    return false;
}

function rectVsRect(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function circleVsRect(c, r) {
    const nx = clamp(c.cx, r.x, r.x + r.w);
    const ny = clamp(c.cy, r.y, r.y + r.h);
    const dx = c.cx - nx;
    const dy = c.cy - ny;
    return dx * dx + dy * dy <= c.r * c.r;
}

function circleVsCircle(a, b) {
    const dx = a.cx - b.cx;
    const dy = a.cy - b.cy;
    const rr = a.r + b.r;
    return dx * dx + dy * dy <= rr * rr;
}

/** Overlap test between two world-space shapes (rect / circle /
 *  convex polygon). */
export function shapeVsShape(a, b) {
    if (a.type === 'rect' && b.type === 'rect') return rectVsRect(a, b);
    if (a.type === 'circle' && b.type === 'circle') return circleVsCircle(a, b);
    if (a.type === 'circle' && b.type === 'rect') return circleVsRect(a, b);
    if (a.type === 'rect' && b.type === 'circle') return circleVsRect(b, a);

    // Polygon paths — rects are promoted to their corner polygon so the
    // SAT routine handles rect-vs-poly and poly-vs-poly uniformly.
    if (a.type === 'polygon' || b.type === 'polygon') {
        if (a.type === 'circle') return circleVsPolygon(a, b.points || rectToPolygon(b));
        if (b.type === 'circle') return circleVsPolygon(b, a.points || rectToPolygon(a));
        const pa = a.type === 'polygon' ? a.points : rectToPolygon(a);
        const pb = b.type === 'polygon' ? b.points : rectToPolygon(b);
        return polygonsOverlap(pa, pb);
    }
    return rectVsRect(a, b);
}

/**
 * Shape-aware overlap between two collision objects. Reads each side's
 * `collisionShape` ({type, scale}); a missing shape defaults to a
 * full-box rect, which is identical to the old AABB test.
 *
 * @param {{x,y,w,h,collisionShape?}} a
 * @param {{x,y,w,h,collisionShape?}} b
 */
export function shapesOverlap(a, b) {
    return shapeVsShape(worldShape(a, a.collisionShape), worldShape(b, b.collisionShape));
}

// ── One-way platforms ─────────────────────────────────────────────

/** Pixel slack so a fast-falling body still snaps onto a thin one-way
 *  ledge instead of tunnelling through it on a single frame, and so
 *  "resting on top" detection tolerates sub-pixel jitter. */
export const ONE_WAY_TOLERANCE = 2;

/**
 * Should a downward-moving body be stopped by a one-way platform top?
 * True only when the body's feet were AT or ABOVE the platform's top
 * edge before this frame's move — i.e. it is landing on the surface,
 * not rising up through it.
 *
 * @param {number} prevBottom  the body's bottom edge BEFORE the move
 * @param {number} tileTop      the platform's top edge (cell row · cellSize)
 */
export function oneWayBlocksDown(prevBottom, tileTop) {
    return prevBottom <= tileTop + ONE_WAY_TOLERANCE;
}
